/**
 * The phone could only throw one kind of grenade (Stage 143).
 *
 * The desktop throws with G and cycles the type with Q, and the HUD lists `FRAG 2 · SMOKE 1 ·
 * EMP 1` with the selected one lit. The touch build hides that list ("the NADE pad carries the
 * selected one") and has no cycle at all, so a phone was stuck on whichever type the file spawned
 * with: it could never throw smoke or EMP, and it could not see how many it had left.
 *
 * The two pads say what they do. The throw pad names what it will throw and how many are left;
 * the cycle pad names what the next tap selects and how many of those are left. The sim cycles
 * blindly through the list (`(sel + 1) % n`), whatever the counts, so the label does too: the pad
 * tells the truth about where the tap goes, empty or not.
 *
 * The count (Stage 881). The row painted 0 in the same yellow as a grenade you still
 * have. An empty gun is already magenta.
 *
 * The next one (Stage 882). The row marked the grenade in hand. It did not mark the
 * one the next cycle selects. The phone's cycle pad already does.
 *
 * The phone (Stage 883). The row is hidden there, so a spent zero stayed in the
 * button's own colour. The same empty mark the row uses now sits on the pad.
 *
 * A count you still have (Stage 884). The row paints that number yellow. On the
 * phone it stayed the button's cyan, the same colour as the name.
 */

/** the index the sim moves to on the next cycle: the list's next, wrapping, whatever the counts */
export function nextGrenade(sel: number, count: number): number {
  if (count <= 0) return 0;
  return ((((sel % count) + count) % count) + 1) % count;
}

/** The count on the row. Magenta at none, the same empty a gun uses. Yellow while any are left. */
export function nadeCountHtml(count: number): string {
  const n = Number.isFinite(count) ? count : 0;
  return `<i${n <= 0 ? ` class="empty"` : ""}>${n}</i>`;
}

/** One grenade on the row. The selected one stays lit. The next cycle wears a mark, the same one the phone's pad uses. */
export function nadeSlot(name: string, count: number, on: boolean, next: boolean): string {
  const mark = next && !on ? "▸ " : "";
  const cls = on ? "on" : next ? "next" : "";
  return `<span${cls ? ` class="${cls}"` : ""}>${mark}${name} ${nadeCountHtml(count)}</span>`;
}

/** the two pads' labels: what a tap throws, and what the next cycle selects */
export function grenadePad(sel: number, counts: readonly number[], names: readonly string[]): { throwLabel: string; cycleLabel: string } {
  const n = Math.min(counts.length, names.length);
  if (n === 0) return { throwLabel: "NADE", cycleLabel: "NADE" };
  const at = ((sel % n) + n) % n;
  const next = (at + 1) % n;
  return { throwLabel: `${names[at]} ${counts[at]}`, cycleLabel: `▸${names[next]} ${counts[next]}` };
}

/** The same words, with the count marked. A phone hides the row, so the zero has to be magenta here. */
export function grenadePadHtml(sel: number, counts: readonly number[], names: readonly string[]): { throwHtml: string; cycleHtml: string } {
  const n = Math.min(counts.length, names.length);
  if (n === 0) return { throwHtml: "NADE", cycleHtml: "NADE" };
  const at = ((sel % n) + n) % n;
  const next = (at + 1) % n;
  const shown = (i: number): string => nadeCountHtml(Number.isFinite(counts[i]) ? counts[i]! : 0);
  return { throwHtml: `${names[at]} ${shown(at)}`, cycleHtml: `▸${names[next]} ${shown(next)}` };
}
